import { DropDownOption, DropDownRestrictionSection, EnableWhen, KeyValuePair, ValidationJson } from 'src/interfaces/utility-interfaces';

import { AppConstants } from 'src/constants/constants';
import { BaseInput } from '../shared/models/base-input';
import { CheckboxInput } from '../shared/models/checkbox-input';
import { DateInput } from '../shared/models/date-input';
import { DateTimeInput } from '../shared/models/date-time-input';
import { DisplayInput } from '../shared/models/display-input';
import { DropdownInput } from '../shared/models/dropdown-input';
import { FieldEmitEvent } from './../../interfaces/utility-interfaces';
import { Injectable } from '@angular/core';
import { StringInput } from '../shared/models/string-input';
import { UtilService } from 'src/app/services/util.service';
import { fhirR4 } from '@smile-cdr/fhirts';

@Injectable({
  providedIn: 'root'
})
export class InputService {

  public initialWaitLocation = '';
  public initialNonPciHospital = '';
  public initialReferringPhysician = '';
  public initialResponsibleHcp = '';
  public initialServiceLocation = '';
  public initialTreatingHcps: Array<KeyValuePair> = [];
  public initialEntryCategory = '';
  public initialServiceArea = '';
  public initialServiceDetail1 = '';
  public initialServiceDetail2 = '';

  fullEntryCategoryOptions: DropDownOption[] = [];
  fullServiceAreaOptions: DropDownOption[] = [];
  fullServiceDetail1Options: DropDownOption[] = [];
  fullServiceDetail2Options: DropDownOption[] = [];

  public currentChildValue = '';
  public changedCrossFormFields: {fieldEvent: FieldEmitEvent, updateStatus: boolean}[] = [];
  public directUIChangedField = '';

  constructor(private utilService: UtilService) { }

  resetServiceFieldOptions() {
    this.fullEntryCategoryOptions = [];
    this.fullServiceAreaOptions = [];
    this.fullServiceDetail1Options = [];
    this.fullServiceDetail2Options = [];
  }

  loadServiceFormCodes(questionnaire: fhirR4.Questionnaire ) {
    this.resetServiceFieldOptions();
    for (const item of questionnaire?.item) {
      if (item.linkId === AppConstants.entryCategoryLinkId && item.answerOption) {
        this.fullEntryCategoryOptions = item.answerOption ? this.getOptions(item.answerOption) : [];
      }
      if (item.linkId === AppConstants.serviceAreaLinkId && item.answerOption) {
        this.fullServiceAreaOptions = item.answerOption ? this.getOptions(item.answerOption) : [];
      }
      if (item.linkId.startsWith(AppConstants.serviceDetail1LinkId) && item.answerOption) {
          this.fullServiceDetail1Options = [ ... new Set( this.fullServiceDetail1Options.concat(this.getOptions(item.answerOption)))];
      }
      if (item.linkId.startsWith(AppConstants.serviceDetail2LinkId) && item.answerOption) {
        this.fullServiceDetail2Options = [ ... new Set( this.fullServiceDetail2Options.concat(this.getOptions(item.answerOption)))];
      }
    }
  }

  buildFormData(
    questionaire: fhirR4.Questionnaire, validationList?: ValidationJson,
    dropDownRestriction?: DropDownRestrictionSection): BaseInput<any>[] {
    const inputs: BaseInput<any>[] = [];
    let offlistingFields: string[] = [];

    if (questionaire.name === 'Offlisting') {
      offlistingFields = questionaire.item.map(item => item.linkId);
    }

    for (const item of questionaire.item) {
      switch (item.type.toString()) {
        case 'choice':
          inputs.push(new DropdownInput({
            linkId: item.linkId,
            label: item.text,
            options: item.answerOption ? this.getOptions(item.answerOption) : [],
            enableWhen: item.enableWhen ? this.getEnableWhen(item.enableWhen) : [],
            required: item.required,
            show: !item.enableWhen,
            placeholder: item.initial ? item.initial[0].valueString : '',
            type: this.checkOptionType(item),
            enableBehavior: item.enableBehavior,
            leftIdent: offlistingFields.some((constantFieldId: string) => item.linkId === constantFieldId) &&
                    !AppConstants.repeatableProceduralSubsection.some((constantFieldId: string) => item.linkId === constantFieldId)
          }
          ));
          if (questionaire.name === 'Service') {
            if (item.linkId === AppConstants.serviceAreaLinkId && item.answerOption) {
              this.fullServiceAreaOptions = item.answerOption ? this.getOptions(item.answerOption) : [];
            }
            if (item.linkId.startsWith(AppConstants.serviceDetail1LinkId) && item.answerOption) {
              this.fullServiceDetail1Options = this.fullServiceDetail1Options.concat(this.getOptions(item.answerOption));
            }
            if (item.linkId.startsWith(AppConstants.serviceDetail2LinkId) && item.answerOption) {
              this.fullServiceDetail2Options = this.fullServiceDetail2Options.concat(this.getOptions(item.answerOption));
            }
          }
          break;
        case 'open-choice':
          inputs.push(new DropdownInput({
            linkId: item.linkId,
            label: item.text,
            options: item.answerOption ? this.getOptions(item.answerOption) : [],
            show: true,
            required: item.required,
            placeholder: item.initial ? item.initial[0].valueString : '-',
            type: this.checkOptionType(item),
            enableBehavior: item.enableBehavior,
            leftIdent: offlistingFields.some((constantFieldId: string) => item.linkId === constantFieldId) &&
            !AppConstants.repeatableProceduralSubsection.some((constantFieldId: string) => item.linkId === constantFieldId)
          }
          ));
          break;
        case 'display':
          inputs.push(new DisplayInput({
            linkId: item.linkId,
            label: item.text,
            show: item.required == false ? false : true,
          }));
          break;
        case 'date':
          inputs.push(new DateInput({
            linkId: item.linkId,
            label: item.text,
            enableWhen: item.enableWhen ? this.getEnableWhen(item.enableWhen) : [],
            halfLine: AppConstants.addableAcceptanceFields.some(
              (constantFieldId: string) => item.linkId.includes(constantFieldId)),
            required: item.required,
            show: !item.enableWhen || this.checkEnabledWhen(this.getEnableWhen(item.enableWhen)),
            placeholder: item.initial ? item.initial[0].valueString : '',
            related: item.linkId.includes(AppConstants.DARTFieldLinkId) ? AppConstants.DARTFieldLinkId : ''
          }));
          break;
        case 'dateTime':
          inputs.push(new DateTimeInput({
            linkId: item.linkId,
            label: item.text,
            enableWhen: item.enableWhen ? this.getEnableWhen(item.enableWhen) : [],
            required: item.required,
            show: !item.enableWhen,
            placeholder: item.initial ? item.initial[0].valueString : '',
            dataMissingReason: ''
          }));
          break;
        case 'string':
          inputs.push(new StringInput({
            linkId: item.linkId,
            label: item.text,
            enableWhen: item.enableWhen ? this.getEnableWhen(item.enableWhen) : [],
            required: item.required,
            show: !item.readOnly && !item.enableWhen,
            readonly: item.readOnly,
            placeholder: item.initial ? item.initial[0].valueString : '',
            leftIdent: offlistingFields.some((constantFieldId: string) => item.linkId === constantFieldId) &&
                    !AppConstants.repeatableProceduralSubsection.some((constantFieldId: string) => item.linkId === constantFieldId)
          }));
          break;
        case 'boolean':
          const isrequired = item.required == false ? false : true;
          inputs.push(new CheckboxInput({
            linkId: item.linkId,
            label: item.text,
            enableWhen: item.enableWhen ? this.getEnableWhen(item.enableWhen) : [],
            required: item.required,
            show: !item.readOnly && !item.enableWhen && isrequired,
          }));
          break;
      }
    }
    if (validationList) {
      this.getValidationAndPopulate(inputs, validationList);
    }
    if (dropDownRestriction) {
      this.getDropDownRestrictionAndPopulate(inputs, dropDownRestriction);
    }
    return inputs;
  }

  getValidationAndPopulate(formData: BaseInput<any>[], validationList: ValidationJson) {
    for (const validationObj of validationList.validations) {
      for (const field of formData) {
        if (this.utilService.fieldIdMatchSetting(field.linkId , validationObj.linkId)) {
          field.validation = {
            type: validationObj.type,
            min: validationObj.min || null,
            max: validationObj.max || null,
            reg: validationObj.reg || null,
            patternDescription: validationObj.patternDescription || null,
            warning: validationObj.warning || null,
          };
          if (validationObj.disableWhen) {
            field.disableWhen = [];
            field.halfLine = true;
            for (const item of validationObj.disableWhen) {
              field.disableWhen.push({
                operator: item.operator,
                question: item.question,
                value: item.value,
                selfValue: item.selfValue,
              });
              if (item.operator === 'blank') {
                field.disabled = true;
              }
            }
          }
        }
      }
    }
  }

  getDropDownRestrictionAndPopulate(formData: BaseInput<any>[], dropDownRestriction: DropDownRestrictionSection) {
    for (const field of formData) {
      if (field.controlType === 'dropdown') {
        for (const key of Object.keys(dropDownRestriction)) {
          if (field.linkId.includes(key) && field.linkId[0] === key[0]) {
            (field as DropdownInput).dropdownRestrictions = [];
            for (const restriction of dropDownRestriction[key]) {
              (field as DropdownInput).dropdownRestrictions.push({
                type: restriction.type,
                linkId: restriction.linkId,
                filteredList: restriction.filteredList,
                value: restriction.value,
                notValue: restriction.notValue,
                filterValue: restriction.filterValue,
              });
            }
          }
        }
      }
    }
  }

  getEnableWhen(enableWhenList: fhirR4.QuestionnaireEnableWhen[]): EnableWhen[] {
    const returnList: EnableWhen[] = [];
    for (const enableWhen of enableWhenList) {
      returnList.push({
        operator: enableWhen.operator,
        question: enableWhen.question,
        answer: (enableWhen.operator === '=' || enableWhen.operator === '!=') ? enableWhen.answerCoding.code : ''
      });
    }
    return returnList;
  }

  checkEnabledWhen(enableWhenList: EnableWhen[]): boolean {
    for (const enableWhen of enableWhenList) {
      if (enableWhen.operator === '=') {
        return false;
      }
    }
    return true;
  }

  checkOptionType(item: fhirR4.QuestionnaireItem): string {
    if (item.answerOption) {
      for (const answerOption of item.answerOption) {
        if (answerOption.valueString) {
          return 'string';
        }
      }
      return 'code';
    } else {
      return 'dynamic';
    }
  }

  getOptions(answerOptionList: fhirR4.QuestionnaireAnswerOption[]): DropDownOption[] {
    const returnList: DropDownOption[] = [];
    for (const answerOption of answerOptionList) {
      if (answerOption.valueString) {
        const displayText = answerOption.valueString + ' (' + answerOption.valueString.toLowerCase() + ')';
        returnList.push({
          code: answerOption.valueString,
          display: displayText
        });
      }
      if (answerOption.valueCoding) {
        let displayText: any;
        if(answerOption.valueCoding.display) {
          displayText =  answerOption.valueCoding.display + (answerOption.valueCoding.code ? ' (' + answerOption.valueCoding.code + ')' : '')
        } else {
           displayText = answerOption.valueCoding.code;
        }
        returnList.push({
          code: answerOption.valueCoding.code,
          display: displayText
        });
      }
    }
    return returnList;
  }
}
